import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  QrCode,
  ArrowRight,
  Smartphone,
  Sparkles,
  Utensils,
  CheckCircle2,
  Users,
  Store,
  Layers,
  Zap,
  DollarSign,
  Palette,
  BarChart3,
  ExternalLink,
  ChefHat,
} from "lucide-react";
import { apiPublic } from "@/lib/api";

export default function HomePage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    cafes_completed: 50,
    customers_connected: 2500,
    qr_menus_created: 50,
    tables_connected: 500,
  });
  const [cafes, setCafes] = useState([]);
  const [loadingCafes, setLoadingCafes] = useState(true);

  useEffect(() => {
    // Load dynamic statistics
    apiPublic.get("/public/stats")
      .then(({ data }) => setStats(data))
      .catch(() => {});

    // Load showcase cafes
    apiPublic.get("/public/cafes")
      .then(({ data }) => setCafes(data))
      .catch(() => {})
      .finally(() => setLoadingCafes(false));
  }, []);

  return (
    <div className="space-y-24 pb-20">
      {/* 1. HERO SECTION */}
      <section className="relative overflow-hidden bg-slate-950 text-white pt-16 pb-24 md:pt-24 md:pb-32">
        {/* Glowing background circles */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/20 rounded-full blur-[140px] pointer-events-none animate-glow" />
        <div className="absolute bottom-0 right-10 w-[300px] h-[300px] bg-accent/20 rounded-full blur-[100px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-xs font-mono text-primary font-medium tracking-wide">
                <Sparkles className="w-3.5 h-3.5" />
                <span>Next-Gen Digital Menu Tech</span>
              </div>

              <h1 className="font-heading text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1]">
                Turn Your Restaurant Menu Into <span className="bg-gradient-to-r from-primary via-accent to-amber-300 bg-clip-text text-transparent">One Simple Scan.</span>
              </h1>

              <p className="text-slate-300 text-lg sm:text-xl max-w-2xl mx-auto lg:mx-0 font-normal leading-relaxed">
                Tablezy helps cafes and restaurants create beautiful QR-based digital menus that customers can access instantly from their phones.
              </p>

              {/* Dual CTAs */}
              <div className="pt-4 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                <Link
                  to="/contact"
                  className="w-full sm:w-auto h-14 px-8 rounded-full bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-3 text-base shadow-xl shadow-primary/30 hover:bg-primary/90 hover:scale-[1.02] active:scale-95 transition-all"
                >
                  <span>Create Your QR Menu</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>

                <a
                  href="#our-work"
                  className="w-full sm:w-auto h-14 px-8 rounded-full bg-white/10 hover:bg-white/15 text-white font-medium flex items-center justify-center gap-2 text-base border border-white/20 backdrop-blur-sm transition-all"
                >
                  <Utensils className="w-4 h-4 text-primary" />
                  <span>See Our Work</span>
                </a>
              </div>

              {/* Micro Trust indicators */}
              <div className="pt-6 flex flex-wrap items-center justify-center lg:justify-start gap-6 text-xs text-slate-400 font-medium">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" /> No App Download Required
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Live Instant Menu Updates
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Table-Specific QR Codes
                </span>
              </div>
            </div>

            {/* Right Visual Representation (Smartphone + QR Card) */}
            <div className="lg:col-span-5 relative flex justify-center">
              <div className="relative w-full max-w-[340px] sm:max-w-[380px]">
                {/* Phone mockup frame */}
                <div className="relative bg-slate-900 border-4 border-slate-700/80 rounded-[40px] shadow-2xl overflow-hidden p-3 shadow-primary/20 hover:-translate-y-1 transition-transform duration-300">
                  <div className="w-full h-11 bg-slate-950 rounded-t-[30px] flex items-center justify-center px-4">
                    <div className="w-20 h-4 bg-slate-800 rounded-full" />
                  </div>

                  {/* Phone screen preview */}
                  <div className="bg-slate-950 text-white rounded-[24px] p-4 space-y-4 font-body border border-slate-800">
                    <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/20 text-primary grid place-items-center font-heading text-xs font-bold">CL</div>
                        <div>
                          <div className="font-heading font-bold text-xs">Carolina Lounge</div>
                          <div className="text-[9px] text-emerald-400 font-mono flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" /> Table #05
                          </div>
                        </div>
                      </div>
                      <span className="text-[10px] bg-primary/20 text-primary px-2 py-0.5 rounded-full font-mono">Digital Menu</span>
                    </div>

                    {/* Mock category tabs */}
                    <div className="flex gap-1.5 overflow-x-auto no-scrollbar text-[10px]">
                      <span className="px-3 py-1 rounded-full bg-primary text-white font-medium">All Dishes</span>
                      <span className="px-3 py-1 rounded-full bg-slate-800 text-slate-300">Coffee</span>
                      <span className="px-3 py-1 rounded-full bg-slate-800 text-slate-300">Pizza</span>
                    </div>

                    {/* Mock menu item card */}
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-2.5 flex gap-3">
                      <img
                        src="https://images.unsplash.com/photo-1461023058943-07fcbe16d735?w=200&auto=format&fit=crop"
                        alt="Cold Brew"
                        className="w-14 h-14 rounded-xl object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-heading font-semibold text-xs flex justify-between">
                          <span>Artisanal Cold Brew</span>
                          <span className="font-mono text-primary">₹180</span>
                        </div>
                        <p className="text-[10px] text-slate-400 line-clamp-1 mt-0.5">16-hr slow steeped specialty coffee</p>
                        <span className="text-[9px] text-emerald-400 font-mono mt-1 inline-block">🟢 Veg</span>
                      </div>
                    </div>

                    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-2.5 flex gap-3">
                      <img
                        src="https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=200&auto=format&fit=crop"
                        alt="Pizza"
                        className="w-14 h-14 rounded-xl object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-heading font-semibold text-xs flex justify-between">
                          <span>Truffle Pizza</span>
                          <span className="font-mono text-primary">₹420</span>
                        </div>
                        <p className="text-[10px] text-slate-400 line-clamp-1 mt-0.5">Wild mushroom & mozzarella</p>
                        <span className="text-[9px] text-emerald-400 font-mono mt-1 inline-block">🟢 Veg</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating Table QR Card Badge */}
                <div className="absolute -bottom-6 -left-6 sm:-left-10 bg-white text-slate-900 p-4 rounded-2xl shadow-2xl border border-slate-200 flex items-center gap-4 animate-bounce duration-1000">
                  <div className="bg-slate-900 p-2 rounded-xl text-white">
                    <QrCode className="w-10 h-10 stroke-[1.8]" />
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-primary font-bold">SCAN ME</div>
                    <div className="font-heading font-bold text-sm">Table #05 QR Card</div>
                    <div className="text-[11px] text-slate-500">Tap or Scan with Camera</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. DYNAMIC STATISTICS SECTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-slate-900 text-white rounded-3xl p-8 sm:p-12 shadow-2xl relative overflow-hidden">
          <div className="absolute -right-10 -bottom-10 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />

          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary font-semibold">Live Platform Traction</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2">Trusted by Leading Dining Spaces</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-y sm:divide-y-0 sm:divide-x divide-slate-800">
            <div className="pt-4 sm:pt-0">
              <div className="flex items-center justify-center gap-2 text-primary mb-2">
                <Store className="w-5 h-5" />
              </div>
              <div className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white">
                {stats.cafes_completed}+
              </div>
              <div className="text-xs sm:text-sm font-medium text-slate-400 mt-1">Cafes & Restaurants</div>
            </div>

            <div className="pt-4 sm:pt-0">
              <div className="flex items-center justify-center gap-2 text-primary mb-2">
                <Users className="w-5 h-5" />
              </div>
              <div className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white">
                {stats.customers_connected.toLocaleString()}+
              </div>
              <div className="text-xs sm:text-sm font-medium text-slate-400 mt-1">Customers Connected</div>
            </div>

            <div className="pt-4 sm:pt-0">
              <div className="flex items-center justify-center gap-2 text-primary mb-2">
                <QrCode className="w-5 h-5" />
              </div>
              <div className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white">
                {stats.qr_menus_created}+
              </div>
              <div className="text-xs sm:text-sm font-medium text-slate-400 mt-1">QR Menus Created</div>
            </div>

            <div className="pt-4 sm:pt-0">
              <div className="flex items-center justify-center gap-2 text-primary mb-2">
                <Layers className="w-5 h-5" />
              </div>
              <div className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white">
                {stats.tables_connected}+
              </div>
              <div className="text-xs sm:text-sm font-medium text-slate-400 mt-1">Tables Connected</div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. HOW WE WORK SECTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary font-semibold">Simple 4-Step Process</span>
          <h2 className="font-heading text-3xl sm:text-5xl font-bold text-slate-900">How Tablezy Works</h2>
          <p className="text-slate-600 text-base sm:text-lg">
            From contact to custom QR codes on every table in less than 48 hours.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Step 1 */}
          <div className="bg-card border border-border rounded-3xl p-6 relative hover:-translate-y-1 transition-all shadow-sm">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary grid place-items-center font-heading font-bold text-xl mb-4">
              01
            </div>
            <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">Step 1 — Contact Us</h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Cafe or restaurant owner submits their contact details and menu requirements.
            </p>
          </div>

          {/* Step 2 */}
          <div className="bg-card border border-border rounded-3xl p-6 relative hover:-translate-y-1 transition-all shadow-sm">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary grid place-items-center font-heading font-bold text-xl mb-4">
              02
            </div>
            <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">Step 2 — We Create Your Menu</h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Our expert design team crafts a high-resolution, responsive digital menu.
            </p>
          </div>

          {/* Step 3 */}
          <div className="bg-card border border-border rounded-3xl p-6 relative hover:-translate-y-1 transition-all shadow-sm">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary grid place-items-center font-heading font-bold text-xl mb-4">
              03
            </div>
            <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">Step 3 — Generate QR Codes</h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              High quality, table-assigned QR code graphics are generated for print cards.
            </p>
          </div>

          {/* Step 4 */}
          <div className="bg-card border border-border rounded-3xl p-6 relative hover:-translate-y-1 transition-all shadow-sm">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary grid place-items-center font-heading font-bold text-xl mb-4">
              04
            </div>
            <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">Step 4 — Customers Scan</h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Diners scan the table QR code and browse your full menu instantly on their phone.
            </p>
          </div>
        </div>
      </section>

      {/* 4. "CAFES WE HAVE MADE FOR" SHOWCASE SECTION */}
      <section id="our-work" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4 border-b border-border pb-6">
          <div>
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary font-semibold">Portfolio & Clients</span>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold text-slate-900 mt-1">Cafes We Have Made For</h2>
          </div>
          <Link
            to="/our-work"
            className="text-primary font-semibold text-sm flex items-center gap-1 hover:underline"
          >
            <span>View All Client Work</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {loadingCafes ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-80 rounded-3xl bg-slate-200 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cafes.map((cafe) => (
              <article
                key={cafe.id}
                className="group bg-card border border-border rounded-3xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col"
              >
                {/* Cover Image */}
                <div className="h-48 bg-slate-900 relative overflow-hidden">
                  <img
                    src={cafe.cover_image_url || "https://images.unsplash.com/photo-1542372147193-a7aca54189cd?w=800&auto=format&fit=crop"}
                    alt={cafe.cafe_name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />
                  
                  {/* Table Pill Badge */}
                  <span className="absolute top-4 right-4 bg-slate-900/90 backdrop-blur-md text-white text-xs font-mono px-3 py-1 rounded-full border border-white/20">
                    {cafe.table_count} Tables
                  </span>
                </div>

                {/* Body */}
                <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <img
                        src={cafe.logo_url || "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=200&auto=format&fit=crop"}
                        alt="Logo"
                        className="w-10 h-10 rounded-xl object-cover border border-border"
                      />
                      <div>
                        <h3 className="font-heading text-xl font-bold text-slate-900 leading-tight">
                          {cafe.cafe_name}
                        </h3>
                        <p className="text-xs text-primary font-medium">{cafe.location}</p>
                      </div>
                    </div>
                    <p className="text-slate-600 text-sm line-clamp-2">
                      {cafe.description}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="pt-2 border-t border-border flex items-center justify-between">
                    <Link
                      to={cafe.menu_url || `/menu/${cafe.slug}`}
                      className="w-full h-11 rounded-2xl bg-slate-900 hover:bg-primary text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
                    >
                      <ChefHat className="w-4 h-4" />
                      <span>View Menu</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* 5. WHY TABLEZY SECTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary font-semibold">Key Advantages</span>
          <h2 className="font-heading text-3xl sm:text-5xl font-bold text-slate-900">Why Restaurant Owners Choose Tablezy</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Card 1 */}
          <div className="bg-card border border-border rounded-3xl p-6 space-y-3 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/10 text-amber-600 grid place-items-center">
              <Smartphone className="w-6 h-6" />
            </div>
            <h3 className="font-heading text-xl font-bold text-slate-900">📱 No App Required</h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Customers can open the menu directly from their phone browser without downloading any apps.
            </p>
          </div>

          {/* Card 2 */}
          <div className="bg-card border border-border rounded-3xl p-6 space-y-3 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary grid place-items-center">
              <Zap className="w-6 h-6" />
            </div>
            <h3 className="font-heading text-xl font-bold text-slate-900">⚡ Instant Access</h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Scan the table QR code and access high-resolution dish pictures and prices instantly.
            </p>
          </div>

          {/* Card 3 */}
          <div className="bg-card border border-border rounded-3xl p-6 space-y-3 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 text-emerald-600 grid place-items-center">
              <Layers className="w-6 h-6" />
            </div>
            <h3 className="font-heading text-xl font-bold text-slate-900">📋 Easy Menu Updates</h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Restaurant owners can update menu items, prices & sold-out items without reprinting paper menus.
            </p>
          </div>

          {/* Card 4 */}
          <div className="bg-card border border-border rounded-3xl p-6 space-y-3 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 rounded-2xl bg-blue-500/10 text-blue-600 grid place-items-center">
              <DollarSign className="w-6 h-6" />
            </div>
            <h3 className="font-heading text-xl font-bold text-slate-900">💰 Save Printing Cost</h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Reduce recurring paper menu printing expenses every time you change prices or seasonal items.
            </p>
          </div>

          {/* Card 5 */}
          <div className="bg-card border border-border rounded-3xl p-6 space-y-3 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 rounded-2xl bg-purple-500/10 text-purple-600 grid place-items-center">
              <Palette className="w-6 h-6" />
            </div>
            <h3 className="font-heading text-xl font-bold text-slate-900">🎨 Custom Design</h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Menus and QR graphics are tailored with your cafe's logo, brand colors, and aesthetic.
            </p>
          </div>

          {/* Card 6 */}
          <div className="bg-card border border-border rounded-3xl p-6 space-y-3 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 text-indigo-600 grid place-items-center">
              <BarChart3 className="w-6 h-6" />
            </div>
            <h3 className="font-heading text-xl font-bold text-slate-900">📊 Digital Experience</h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Give your diners a clean, modern touchless restaurant experience that impresses every guest.
            </p>
          </div>
        </div>
      </section>

      {/* 6. FINAL CTA SECTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 text-white rounded-3xl p-10 sm:p-16 text-center space-y-6 relative overflow-hidden border border-slate-800 shadow-2xl">
          <div className="w-16 h-16 rounded-2xl bg-primary/20 text-primary grid place-items-center mx-auto mb-2">
            <QrCode className="w-8 h-8 stroke-[2]" />
          </div>

          <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight max-w-2xl mx-auto">
            Ready to Make Your Menu Digital?
          </h2>

          <p className="text-slate-300 text-base sm:text-xl max-w-xl mx-auto">
            Give your customers a faster, cleaner and smarter way to explore your menu.
          </p>

          <div className="pt-4">
            <Link
              to="/contact"
              className="inline-flex h-14 px-8 rounded-full bg-primary text-primary-foreground font-semibold items-center gap-3 text-base hover:bg-primary/90 hover:scale-105 active:scale-95 transition-all shadow-xl shadow-primary/30"
            >
              <span>Create Your QR Menu</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
